// GL01 합배송출고 — moms-be 호출부. 1.0 공통 그리드(/api/common/gridsub · /api/grid/search ·
// /api/grid/save)와 internal/data/send 는 쓰지 않는다.
import request, { getExcel } from '@/utils/request'
import { menuCodeHeaders } from '@/utils/menuCodeHeaders'
import type {
  ConfirmOutboundReq,
  ConfirmOutboundRes,
  ConsolOutboundDownloadReq,
  ConsolOutboundRowDto,
  ConsolOutboundSaveReq,
  ConsolOutboundSaveRes,
  ConsolOutboundSearchReq,
  PageInfoItems,
} from './types'

/** 조회 (LOGISPRD-9603) — UI_PAIRORDER. shipperCode 필수, size 최대 1000. */
export const searchConsolOutbounds = (params: ConsolOutboundSearchReq) =>
  request.post<ApiResponseV2<PageInfoItems<ConsolOutboundRowDto>>, ConsolOutboundSearchReq>('/api/v1/get-global-pair-orders', params)

/** 저장 (LOGISPRD-9604) — 요청 전체가 단일 트랜잭션. 오류 시 전체 롤백. */
export const saveConsolOutbounds = (params: ConsolOutboundSaveReq) =>
  request.post<ApiResponseV2<ConsolOutboundSaveRes>, ConsolOutboundSaveReq>('/api/v1/save-global-pair-orders', params)

/** 동기화 (LOGISPRD-14646) — 즉시 동기화 요청. 본문 없음, 성공 시 HTTP 202 (실제 반영은 1~2분 뒤).
 * request.post 가 body 인자를 요구해 undefined 를 넘긴다 — axios 는 이 경우 본문을 보내지 않는다. */
export const syncConsolOutbounds = () =>
  request.post<ApiResponseV2<unknown>, undefined>('/api/v1/global-order-collections/pair-order-sync', undefined)

/**
 * 출고확정 / 잔여출고확정 — 같은 엔드포인트를 doType 으로 나눠 쓴다.
 * 응답이 StandardResponse 가 아니라 raw DTO(`{result, message}`)라 meta.result 를 보면 안 된다
 * (1.0 GL01 이 이 차이를 잘못 읽어 100% 실패 팝업을 띄웠던 이력 — LOGISPRD-10895).
 * 1.0 GL01 도 헤더 없이 부른다 — BE 는 X-Menu-Code 가 GL15 일 때만 감사 로그를 남긴다(LOGISPRD-16047).
 */
export const confirmGlobalOutbounds = (params: ConfirmOutboundReq) =>
  request.post<ConfirmOutboundRes, ConfirmOutboundReq>('/api/v1/confirm-global-outbounds', params, menuCodeHeaders('GL15'))

/**
 * 엑셀 다운로드 (LOGISPRD-9606) — 서버 생성 AES-256 암호 zip.
 * password(강도 검증)와 detailedReason(5자 이상)이 없으면 400. 100,000행 초과도 400.
 */
export const downloadConsolOutboundsExcel = async (params: ConsolOutboundDownloadReq) => {
  let res
  try {
    res = await getExcel('/api/v1/get-global-pair-orders/download', { method: 'post', data: params })
  } catch (e) {
    // responseType blob 이라 400 의 meta.message 가 Blob 으로 온다 — JSON 으로 되돌려 사유를 노출한다.
    const resp = (e as { response?: { data?: unknown } })?.response
    if (resp?.data instanceof Blob) {
      try {
        resp.data = JSON.parse(await (resp.data as Blob).text())
      } catch {
        /* JSON 이 아니면 상태코드 폴백을 그대로 둔다 */
      }
    }
    throw e
  }

  const fileName = parseFileName(String(res.headers?.['content-disposition'] ?? ''), '글로벌합배송출고.zip')
  const a = document.createElement('a')
  a.href = URL.createObjectURL(res.data)
  a.download = fileName
  a.click()
  URL.revokeObjectURL(a.href)
}

/** Content-Disposition 의 filename*=utf-8'' 값을 꺼낸다. 없으면 폴백 이름을 쓴다. */
const parseFileName = (disposition: string, fallback: string): string => {
  const utf8 = /filename\*=utf-8''([^;]+)/i.exec(disposition)
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim())
    } catch {
      /* 디코딩 실패 시 폴백 */
    }
  }
  const ascii = /filename="?([^";]+)"?/i.exec(disposition)
  return ascii?.[1]?.trim() || fallback
}
